import React, { useState, useEffect } from 'react';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useScreenType } from '../../hooks/useScreenType';
import { API_URL } from '../../config';

import DesktopProfile from './DesktopProfile';
import MobileProfile from './MobileProfile';

const ProfileScreen: React.FC = () => {
  const { user, logout, token, refreshUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { isMobile } = useScreenType();
  const [isEditing, setIsEditing] = useState(false);
  const [fullName, setFullName] = useState('');
  const [pseudonym, setPseudonym] = useState('');
  const [isOptIn, setIsOptIn] = useState(false);
  const [showRealName, setShowRealName] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [showConfirmReset, setShowConfirmReset] = useState(false);

  useEffect(() => {
    if (user?.full_name) setFullName(user.full_name);
    if (user?.social_profile?.pseudonym) setPseudonym(user.social_profile.pseudonym);
    if (user?.social_profile?.is_opt_in_leaderboard) setIsOptIn(user.social_profile.is_opt_in_leaderboard);
    if (user?.social_profile?.display_real_name_on_forum) setShowRealName(user.social_profile.display_real_name_on_forum);
  }, [user]);

  const handleToggleTheme = async () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    toggleTheme();
    if (token) {
      try {
        await fetch(`${API_URL}/auth/me`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ preferred_theme: newTheme })
        });
        await refreshUser(); // Silent sync
      } catch (e) {
        console.error('Failed to persist theme:', e);
      }
    }
  };

  const handleUpdateSocialSettings = async (newOptIn: boolean, newPseudonym: string, newShowReal?: boolean) => {
    try {
      setLoading(true);
      setErrorMsg('');
      setSuccessMsg('');

      const resSocial = await fetch(`${API_URL}/v1/social/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ 
            pseudonym: newPseudonym || null, 
            is_opt_in_leaderboard: newOptIn,
            display_real_name_on_forum: newShowReal !== undefined ? newShowReal : showRealName
        })
      });

      if (resSocial.ok) {
        setIsOptIn(newOptIn);
        setPseudonym(newPseudonym);
        if (newShowReal !== undefined) setShowRealName(newShowReal);
        
        await refreshUser(); // Sync globally
        setSuccessMsg('Préférences sociales mises à jour.');
        setTimeout(() => setSuccessMsg(''), 3000);
      } else {
        const errorData = await resSocial.json();
        setErrorMsg(errorData.detail || 'Erreur lors de la mise à jour sociale.');
      }
    } catch (e) {
      console.error(e);
      setErrorMsg('Erreur système.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      setErrorMsg('');
      setSuccessMsg('');

      const res = await fetch(`${API_URL}/auth/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ full_name: fullName })
      });

      if (res.ok) {
        setIsEditing(false);
        await refreshUser(); // Sync globally
        setSuccessMsg('Profil mis à jour avec succès.');
        setTimeout(() => setSuccessMsg(''), 3000);
      } else {
        setErrorMsg('Erreur lors de la mise à jour du profil.');
      }
    } catch (e) {
      console.error(e);
      setErrorMsg('Erreur système. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!showConfirmReset) {
      setShowConfirmReset(true);
      return;
    }
    if (!user?.email) return;

    try {
      setLoading(true);
      setErrorMsg('');
      setSuccessMsg('');
      const res = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email })
      });
      if (res.ok) {
        setSuccessMsg("Un email de réinitialisation vous a été envoyé à l'adresse " + user.email);
        setShowConfirmReset(false);
      } else {
        setErrorMsg("Erreur lors de l'envoi de l'email.");
      }
    } catch (e) {
      console.error(e);
      setErrorMsg('Erreur système. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getPlanName = (roleName?: string) => {
    if (!roleName) return 'Gratuit (Non défini)';
    switch (roleName) {
      case 'global_admin': return 'Administrateur Global 👑';
      case 'tenant_admin': return 'Admin. Organisation 🏢';
      case 'trader_advanced': return 'Premium (Avancé) 🚀';
      case 'trader_prudent': return 'Pro (Prudent) 🛡️';
      case 'paid_user': return 'Premium (Pro) 🚀';
      case 'paid_pro_user': return 'Expert (Pro+) 🏆';
      case 'paid_user_pro': return 'Kanea Pro 🏆';
      case 'basic_user': return 'Découverte (Gratuit) 🌱';
      default: return roleName;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'À vie (Sans expiration)';
    return new Date(dateString).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const childProps = {
    user, theme, isEditing, setIsEditing, fullName, setFullName,
    pseudonym, setPseudonym, isOptIn, setIsOptIn, showRealName, setShowRealName, loading,
    successMsg, errorMsg, showConfirmReset,
    handleToggleTheme, handleSaveProfile, handleResetPassword, handleUpdateSocialSettings,
    setShowConfirmReset, logout, getInitials, getPlanName, formatDate,
  };

  return (
    <SiteLayout>
      <Header icon='user' title='Mon Profil' />
      {isMobile ? (
        <MobileProfile {...childProps} />
      ) : (
        <DesktopProfile {...childProps} />
      )}
    </SiteLayout>
  );
};

export default ProfileScreen;
